import type { SourceSegment } from "../api/types";

export const MAX_VIDEO_SECONDS = 4 * 3600;
export const MAX_SOURCE_SECONDS = 8 * 3600;
export const MAX_VIDEO_BYTES = 50 * 1024 ** 3;

/** Elapsed video time; these are positions in a recording, not times of day. */
export function videoTime(seconds: number): string {
  const n = Math.floor(seconds);
  return [Math.floor(n / 3600), Math.floor(n / 60) % 60, n % 60].map((v) => String(v).padStart(2, "0")).join(":");
}

export function parseVideoTime(value: string): number | null {
  if (!/^\d{1,2}:[0-5]\d:[0-5]\d$/.test(value.trim())) return null;
  const [h, m, s] = value.trim().split(":").map(Number);
  return h! * 3600 + m! * 60 + s!;
}

export function videoFileProblem(file: File): string | null {
  if (!/\.(mp4|mov)$/i.test(file.name)) return "Choose an MP4 or MOV video.";
  if (!file.size) return "This file is empty. Choose another video.";
  if (file.size > MAX_VIDEO_BYTES) return "This video is larger than 50 GB. Choose a smaller file.";
  return null;
}

export function selectedPart(start: string, end: string, durationS: number): { segment: SourceSegment | null; problem: string | null } {
  const s = parseVideoTime(start);
  const e = parseVideoTime(end);
  if (s === null || e === null) return { segment: null, problem: "Enter start and end times as HH:MM:SS." };
  if (e <= s) return { segment: null, problem: "Choose an end time after the start time." };
  if (e > durationS) return { segment: null, problem: `Choose an end time within your video (${videoTime(durationS)}).` };
  if (e - s > MAX_VIDEO_SECONDS) return { segment: null, problem: "Choose a part up to 4 hours long." };
  return { segment: { start_ms: s * 1000, end_ms: e * 1000 }, problem: null };
}
